import { useMemo } from 'react';
import { useLoggedInUserContextProvider } from '../../context/LoggedInUserContext';
import { useAdminCollections } from '../../context/AdminCollections';

export const useUserPermissions = () => {
  const { userDetails } = useLoggedInUserContextProvider();
  const { committeeArray } = useAdminCollections();

  const isAdmin =
    userDetails?.roles?.includes('super-admin') ||
    userDetails?.roles?.includes('admin');
  const isSCChair = userDetails?.roles?.includes('committee-chair');
  const isSCSecretary = userDetails?.roles?.includes('secretary');
  const isSCObserver = userDetails?.roles?.includes('council-observer');
  const isCouncilMember =
    userDetails?.roles?.includes('council-member') || false;

  const hasAccess =
    isAdmin || isSCChair || isSCSecretary || isSCObserver || isCouncilMember;

  const userCommittee = useMemo(() => {
    if (isAdmin || isSCSecretary || isCouncilMember) return null;

    if (isSCChair || isSCObserver) {
      return committeeArray?.find((committee) => {
        const isChair =
          committee.attributes.committeeChair?.data?.id === userDetails?.id;

        const isMember = committee.attributes.committeeMembers?.data?.some(
          (member) => member.id === userDetails?.id,
        );

        const isObserver = committee.attributes.councilObservers?.data?.some(
          (observer) => observer.id === userDetails?.id,
        );

        return isChair || isMember || isObserver;
      });
    }

    return null;
  }, [
    isAdmin,
    isSCChair,
    isSCSecretary,
    isSCObserver,
    isCouncilMember,
    committeeArray,
    userDetails?.id,
  ]);

  const userCommittees = useMemo(() => {
    if (isAdmin || isSCSecretary || isCouncilMember) return [];

    if (isSCObserver) {
      return (
        committeeArray?.filter((committee) =>
          committee.attributes.councilObservers?.data?.some(
            (observer) => observer.id === userDetails?.id,
          ),
        ) || []
      );
    }

    if (isSCChair) {
      return (
        committeeArray?.filter((committee) => {
          const isChair =
            committee.attributes.committeeChair?.data?.id === userDetails?.id;

          const isMember = committee.attributes.committeeMembers?.data?.some(
            (member) => member.id === userDetails?.id,
          );

          return isChair || isMember;
        }) || []
      );
    }

    const isSCMember = userDetails?.roles?.includes('committee-member');
    if (isSCMember) {
      return (
        committeeArray?.filter((committee) => {
          const isMember = committee.attributes.committeeMembers?.data?.some(
            (member) => member.id === userDetails?.id,
          );
          return isMember;
        }) || []
      );
    }

    return [];
  }, [
    isAdmin,
    isSCChair,
    isSCSecretary,
    isSCObserver,
    isCouncilMember,
    committeeArray,
    userDetails?.id,
  ]);

  const permissionsLoading = !userDetails;

  return {
    isAdmin,
    isSCChair,
    isSCSecretary,
    isSCObserver,
    isCouncilMember,
    hasAccess,
    userCommittee,
    userCommittees,
    permissionsLoading,
  };
};
